import { Input, Label } from '@kmgestao/ui';

import { formatPhoneBR } from '../../../leads/formatters.js';
import type { FormField } from '../../schemas.js';

export interface TelFieldProps {
  field: FormField;
  value: string;
  onChange: (next: string) => void;
  onBlur?: (() => void) | undefined;
  error?: string | undefined;
  disabled?: boolean | undefined;
  idPrefix?: string | undefined;
}

/**
 * Campo de telefone brasileiro. Aplica máscara progressiva
 * `(XX) XXXXX-XXXX` enquanto o usuário digita — reusa `formatPhoneBR`
 * de `features/leads/formatters.ts` (regra do sistema padronizada no
 * PR #99).
 *
 * `inputmode="tel"` + `autocomplete="tel"` disparam o teclado numérico
 * do iOS/Android no mobile.
 */
export function TelField({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  idPrefix = 'form',
}: TelFieldProps) {
  const inputId = `${idPrefix}-${field.id}`;
  const descId = field.description ? `${inputId}-desc` : undefined;
  const errorId = error ? `${inputId}-err` : undefined;

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>
        {field.label}
        {field.required ? <span className="ml-0.5 text-destructive"> *</span> : null}
      </Label>
      {field.description ? (
        <p id={descId} className="text-xs text-muted-foreground">
          {field.description}
        </p>
      ) : null}
      <Input
        id={inputId}
        type="tel"
        value={value}
        onChange={(e) => onChange(formatPhoneBR(e.target.value))}
        onBlur={onBlur}
        placeholder={field.placeholder ?? '(11) 99999-9999'}
        autoComplete={field.autocomplete ?? 'tel'}
        inputMode="tel"
        maxLength={15}
        hasError={!!error}
        disabled={disabled}
        aria-describedby={[descId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={!!error}
      />
      {error ? (
        <p id={errorId} className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
